import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, updateDoc, deleteDoc, doc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";

const COLLECTION = "avantajBasvurulari";

type Durum = "bekliyor" | "onaylandi" | "reddedildi";

interface Basvuru {
  id: string;
  uyeAd: string;
  uyeTelefon: string;
  kurumAd: string;
  kurumId: string;
  tarih: Timestamp | null;
  durum: Durum;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  bekliyor: { label: "Bekliyor", color: "bg-yellow-100 text-yellow-700" },
  onaylandi: { label: "Onaylandı", color: "bg-green-100 text-green-700" },
  reddedildi: { label: "Reddedildi", color: "bg-red-100 text-red-700" },
};

function formatTarih(t: Timestamp | null): string {
  if (!t || typeof t.toDate !== "function") return "—";
  try {
    return t.toDate().toLocaleDateString("tr-TR");
  } catch {
    return "—";
  }
}

const AvantajBasvurulariPage: React.FC = () => {
  const [rows, setRows] = useState<Basvuru[]>([]);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(false);
  const [filter, setFilter] = useState<string>("hepsi");

  const load = async () => {
    setLoading(true);
    setHata(false);
    try {
      const snap = await getDocs(query(collection(db, COLLECTION), orderBy("tarih", "desc")));
      setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Basvuru[]);
    } catch (e) {
      console.error("Avantaj başvuruları okunamadı:", e);
      setHata(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((a) => filter === "hepsi" || a.durum === filter);

  const updateStatus = async (id: string, durum: Durum) => {
    try {
      await updateDoc(doc(db, COLLECTION, id), { durum });
      setRows((prev) => prev.map((a) => (a.id === id ? { ...a, durum } : a)));
    } catch (e) {
      console.error("Durum güncellenemedi:", e);
      window.alert("Durum güncellenemedi, lütfen tekrar deneyin.");
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Bu başvuru silinsin mi?")) return;
    try {
      await deleteDoc(doc(db, COLLECTION, id));
      setRows((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      console.error("Silinemedi:", e);
      window.alert("Silme başarısız, lütfen tekrar deneyin.");
    }
  };

  const bekleyen = rows.filter((a) => a.durum === "bekliyor").length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Avantaj Başvuruları</h2>
          <p className="text-sm text-gray-500 mt-0.5">{bekleyen} bekleyen, {rows.length} toplam · mobil "Keşfet" başvurularından</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {["hepsi", "bekliyor", "onaylandi", "reddedildi"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                ${filter === f ? "bg-[#8B1428] text-white" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}>
              {f === "hepsi" ? "Hepsi" : STATUS_LABELS[f]?.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-400"><i className="fa-solid fa-circle-notch fa-spin text-3xl" /></div>
      ) : hata ? (
        <div className="bg-red-50 text-red-600 rounded-2xl py-12 text-center text-sm">
          Başvurular yüklenemedi. <button onClick={load} className="underline font-semibold">Tekrar dene</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 py-20 text-center">
          <i className="fa-solid fa-ticket text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Henüz avantaj başvurusu yok</p>
          <p className="text-gray-400 text-sm mt-1">Üyeler Keşfet ekranından başvurduğunda burada görünecek</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Üye</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Telefon</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Kurum</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Tarih</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Durum</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-800">{a.uyeAd}</td>
                  <td className="px-4 py-3 text-gray-500 font-mono hidden sm:table-cell">{a.uyeTelefon}</td>
                  <td className="px-4 py-3 text-gray-700">{a.kurumAd}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs hidden md:table-cell">{formatTarih(a.tarih)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_LABELS[a.durum]?.color ?? "bg-gray-100 text-gray-600"}`}>
                      {STATUS_LABELS[a.durum]?.label ?? a.durum}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {a.durum === "bekliyor" && (
                        <>
                          <button onClick={() => updateStatus(a.id, "onaylandi")} className="text-green-600 hover:text-green-800 text-xs font-semibold">Onayla</button>
                          <button onClick={() => updateStatus(a.id, "reddedildi")} className="text-red-500 hover:text-red-700 text-xs font-semibold">Reddet</button>
                        </>
                      )}
                      <button onClick={() => remove(a.id)} className="text-gray-400 hover:text-red-500 transition-colors"><i className="fa-solid fa-trash text-xs" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">{filtered.length} başvuru gösteriliyor</div>
        </div>
      )}
    </div>
  );
};

export default AvantajBasvurulariPage;
